import z from "zod";

import {
  AudienceTargeting,
  AudienceTargetingFilterCategory,
  AudienceTargetingFilterOperation,
  TargetFilterSchemaBase,
  TargetGroupSchemaBase,
} from "@stw/yup-validators";

import {
  AudienceTargeting as AudienceTargetingGqlType,
  AudienceTargetingFilterCategory as AudienceTargetingFilterCategoryGql,
  AudienceTargetingFilterOperation as AudienceTargetingFilterOperationGql,
} from "~frontend/__generated__/graphql";

const TargetFilterFormSchema = TargetFilterSchemaBase.and(
  z.object({
    // react-hook-form lens.map adds an id to each element
    id: z.string().nullish(),
  })
);
export type TargetingFormFilter = z.infer<typeof TargetFilterFormSchema>;

const TargetGroupFormSchema = TargetGroupSchemaBase.extend({
  // react-hook-form lens.map adds an id to each element
  id: z.string().nullish(),
  clauses: z.array(TargetFilterFormSchema),
});
export type TargetingFormGroup = z.infer<typeof TargetGroupFormSchema>;

export const TargetingFormSchema = z.object({
  targetGroups: z.array(TargetGroupFormSchema),
});
export type TargetingFormValues = z.infer<typeof TargetingFormSchema>;

function convertGqlFilterCategoryToFormValue(
  filterCategoryGql: AudienceTargetingFilterCategoryGql
): AudienceTargetingFilterCategory {
  switch (filterCategoryGql) {
    case AudienceTargetingFilterCategoryGql.CustomField:
      return AudienceTargetingFilterCategory.CustomField;
    case AudienceTargetingFilterCategoryGql.DonationHistory:
      return AudienceTargetingFilterCategory.DonationHistory;
    case AudienceTargetingFilterCategoryGql.Location:
      return AudienceTargetingFilterCategory.Location;
    case AudienceTargetingFilterCategoryGql.SavedSearch:
      return AudienceTargetingFilterCategory.SavedSearch;
    case AudienceTargetingFilterCategoryGql.Engagement:
      return AudienceTargetingFilterCategory.Engagement;
  }
}

function convertGqlFilterOperationToFormValue(
  filterOperationGql: AudienceTargetingFilterOperationGql
): AudienceTargetingFilterOperation {
  switch (filterOperationGql) {
    case AudienceTargetingFilterOperationGql.Equals:
      return "equals";
    case AudienceTargetingFilterOperationGql.NotEquals:
      return "notEquals";
    case AudienceTargetingFilterOperationGql.Gt:
      return "gt";
    case AudienceTargetingFilterOperationGql.Lt:
      return "lt";
    case AudienceTargetingFilterOperationGql.AverageDonationBetween:
      return "averageDonationBetween";
    case AudienceTargetingFilterOperationGql.TotalDonationBetween:
      return "totalDonationBetween";
    case AudienceTargetingFilterOperationGql.LastDonationBetween:
      return "lastDonationBetween";
    case AudienceTargetingFilterOperationGql.AverageDonationGt:
      return "averageDonationGt";
    case AudienceTargetingFilterOperationGql.AverageDonationLt:
      return "averageDonationLt";
    case AudienceTargetingFilterOperationGql.TotalDonationGt:
      return "totalDonationGt";
    case AudienceTargetingFilterOperationGql.TotalDonationLt:
      return "totalDonationLt";
    case AudienceTargetingFilterOperationGql.LastDonationGt:
      return "lastDonationGt";
    case AudienceTargetingFilterOperationGql.LastDonationLt:
      return "lastDonationLt";
    case AudienceTargetingFilterOperationGql.Between:
      return "between";
    case AudienceTargetingFilterOperationGql.Boolean:
      return "boolean";
    case AudienceTargetingFilterOperationGql.Contains:
      return "contains";
    case AudienceTargetingFilterOperationGql.NotContains:
      return "notContains";
    case AudienceTargetingFilterOperationGql.CustomFalse:
      return "customFalse";
    case AudienceTargetingFilterOperationGql.CustomTrue:
      return "customTrue";
    case AudienceTargetingFilterOperationGql.CustomIsNull:
      return "customIsNull";
    case AudienceTargetingFilterOperationGql.HasDonated:
      return "hasDonated";
    case AudienceTargetingFilterOperationGql.NotHasDonated:
      return "notHasDonated";
    case AudienceTargetingFilterOperationGql.HasDonatedToCampaigns:
      return "hasDonatedToCampaigns";
    case AudienceTargetingFilterOperationGql.NotHasDonatedToCampaigns:
      return "notHasDonatedToCampaigns";
    case AudienceTargetingFilterOperationGql.HasMobileDonation:
      return "hasMobileDonation";
    case AudienceTargetingFilterOperationGql.NotHasMobileDonation:
      return "notHasMobileDonation";
    case AudienceTargetingFilterOperationGql.HighestDonationBetween:
      return "highestDonationBetween";
    case AudienceTargetingFilterOperationGql.HighestDonationGt:
      return "highestDonationGt";
    case AudienceTargetingFilterOperationGql.HighestDonationLt:
      return "highestDonationLt";
    case AudienceTargetingFilterOperationGql.IsInAreaCode:
      return "isInAreaCode";
    case AudienceTargetingFilterOperationGql.NotInAreaCode:
      return "notInAreaCode";
    case AudienceTargetingFilterOperationGql.IsInCustomState:
      return "isInCustomState";
    case AudienceTargetingFilterOperationGql.NotInCustomState:
      return "notInCustomState";
    case AudienceTargetingFilterOperationGql.IsInCustomZip:
      return "isInCustomZip";
    case AudienceTargetingFilterOperationGql.NotInCustomZip:
      return "notInCustomZip";
    case AudienceTargetingFilterOperationGql.IsInSavedSearch:
      return "isInSavedSearch";
    case AudienceTargetingFilterOperationGql.IsInCustomZipRadius:
      return "isInCustomZipRadius";
    case AudienceTargetingFilterOperationGql.NotInCustomZipRadius:
      return "notInCustomZipRadius";
    case AudienceTargetingFilterOperationGql.IsInState:
      return "isInState";
    case AudienceTargetingFilterOperationGql.NotInState:
      return "notInState";
    case AudienceTargetingFilterOperationGql.IsInZip:
      return "isInZip";
    case AudienceTargetingFilterOperationGql.NotInZip:
      return "notInZip";
    case AudienceTargetingFilterOperationGql.IsInZipRadius:
      return "isInZipRadius";
    case AudienceTargetingFilterOperationGql.NotInZipRadius:
      return "notInZipRadius";
    case AudienceTargetingFilterOperationGql.IsNull:
      return "isNull";
    case AudienceTargetingFilterOperationGql.IsRecurringDonor:
      return "isRecurringDonor";
    case AudienceTargetingFilterOperationGql.Matches:
      return "matches";
    case AudienceTargetingFilterOperationGql.NotBetween:
      return "notBetween";
    case AudienceTargetingFilterOperationGql.NotOneOf:
      return "notOneOf";
    case AudienceTargetingFilterOperationGql.OneOf:
      return "oneOf";
    case AudienceTargetingFilterOperationGql.NotRecurringDonor:
      return "notRecurringDonor";
    case AudienceTargetingFilterOperationGql.NumberOfDonationsBetween:
      return "numberOfDonationsBetween";
    case AudienceTargetingFilterOperationGql.NumberOfDonationsGt:
      return "numberOfDonationsGt";
    case AudienceTargetingFilterOperationGql.NumberOfDonationsLt:
      return "numberOfDonationsLt";
    case AudienceTargetingFilterOperationGql.RecurringDonationAmountBetween:
      return "recurringDonationAmountBetween";
    case AudienceTargetingFilterOperationGql.RecurringDonationAmountGt:
      return "recurringDonationAmountGt";
    case AudienceTargetingFilterOperationGql.RecurringDonationAmountLt:
      return "recurringDonationAmountLt";
    case AudienceTargetingFilterOperationGql.StateIsNull:
      return "stateIsNull";
    case AudienceTargetingFilterOperationGql.ZipIsNull:
      return "zipIsNull";
    case AudienceTargetingFilterOperationGql.HasReplied:
      return "hasReplied";
    case AudienceTargetingFilterOperationGql.NotHasReplied:
      return "notHasReplied";
    case AudienceTargetingFilterOperationGql.HasRepliedToCampaigns:
      return "hasRepliedToCampaigns";
    case AudienceTargetingFilterOperationGql.NotHasRepliedToCampaigns:
      return "notHasRepliedToCampaigns";
    case AudienceTargetingFilterOperationGql.HasClicked:
      return "hasClicked";
    case AudienceTargetingFilterOperationGql.NotHasClicked:
      return "notHasClicked";
    case AudienceTargetingFilterOperationGql.HasClickedOnCampaigns:
      return "hasClickedOnCampaigns";
    case AudienceTargetingFilterOperationGql.NotHasClickedOnCampaigns:
      return "notHasClickedOnCampaigns";
  }
}

export function convertGqlAudienceTargetingToFormValues(
  audienceTargetingGql: AudienceTargetingGqlType
): AudienceTargeting {
  return {
    targetGroups: audienceTargetingGql.targetGroups.map(targetGroup => ({
      ...targetGroup,
      clauses: targetGroup.clauses.map(clause => ({
        ...clause,
        filterCategory: convertGqlFilterCategoryToFormValue(
          clause.filterCategory
        ),
        filterOperation: convertGqlFilterOperationToFormValue(
          clause.filterOperation
        ),
        filterConfig: {
          ...clause.filterConfig,
          searchBody: clause.filterConfig?.searchBody
            ? convertGqlAudienceTargetingToFormValues(
                clause.filterConfig.searchBody
              )
            : null,
        },
      })),
    })),
  };
}
